import { CardContent, FormLabel, Grid, RadioGroup, TextField } from '@mui/material';
import { AppImageDimension, AppImages } from 'app/appImages';
import { AppTransComponents } from 'app/appTrans';
import { MprcButton, MprcCard, MprcConfirmDialog, MprcContainer, MprcFormSubHeader, MprcStringToHtml } from 'components';
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { ModeratorChannelAutoCompleteDataModel } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { AutoCompleteHierarchicalModel, UnitAutoCompleteHierarchicalModel } from 'components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel';
import { MprcImageSelection } from 'components/MprcImageHandler';
import { MprcRadio } from 'components/MprcRadio';
import { MprcSwitch } from 'components/MprcSwitch';
import { ChannelApprovalMode, ChannelAutoRegisterMode, ChannelRoles, ManagedChannelQueryResponse, UnitAutoCompleteHierarchicalModelDto } from 'generated/CoreApi_v1';
import MediaUtils from 'lib/MediaUtils';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { NIL } from 'uuid';
import { TopicAutoCompleteHierarchical } from 'views/foundation/topic/components/TopicAutoCompleteHierarchical';
import { UnitAutoCompleteHierarchical } from 'views/foundation/unit/components/UnitAutoCompleteHierarchical';
import { ModeratorChannelAutoComplete } from '../../components/ChannelModeratorAutoComplete';

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    name?: string[] | null,
    approvalPassword?: string[] | null,
    thumbnail?: string[] | null,
    moderator?: string[] | null,
    topicSuggestion?: string[] | null,
    unitsDismissed?: string[] | null,
}

interface IProps {
    viewData: ManagedChannelQueryResponse;
    viewErrors: ApiProblemErrors | undefined;
    recordChanged: boolean;

    onFormUpdate: (dto: ManagedChannelQueryResponse) => void;
    onFormValidation: (valid: boolean) => void;
    onError: (error: any) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Formulário principal da edição de Canais
 * @param props
 */
export const EditChannelMainBox: React.FC<IProps> = (props) => {

    const { viewData, viewErrors, recordChanged, onFormUpdate, onFormValidation, onError } = props;

    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();

    const [dialog, setDialog] = useState<any>(null);
    const [formValidation, setFormValidation] = useState<IValidationFields>(
        {
            name: undefined,
            approvalPassword: undefined,
            thumbnail: null,
            moderator: null,
            topicSuggestion: null,
            unitsDismissed: null
        });

    ////////////////////////////////////////////////////////////////////////////

    ///
    const validateForm = (dto: ManagedChannelQueryResponse) => {
        let newFormValidation = { ...formValidation };
        newFormValidation.approvalPassword = dto.dismissApprovalWithPassword === true && (dto.approvalPassword == undefined || dto.approvalPassword.length < 6)
            ? [t("Channel.Edit.PasswordMinLen")]
            : null;

        newFormValidation.name = !dto.name ? [t("Channel.Edit.RequiredField")] : null;
        newFormValidation.moderator = dto.moderator == undefined ? [t("Channel.Edit.RequiredField")] : null;

        setFormValidation(newFormValidation);
    };

    ///
    const handleUpdateRecord = (name: keyof ManagedChannelQueryResponse, v: any) => {

        let newViewData = { ...viewData, [name]: v };

        if (name === "autoRegisterMode") {
            if (v != ChannelAutoRegisterMode.ByApproval) {
                newViewData = {
                    ...viewData, [name]: v,
                    dismissApprovalForUnits: false,
                    dismissApprovalWithPassword: false,
                    approvalPassword: '',
                    allowInvite: false
                };
            }
        }
        else if (name === "dismissApprovalWithPassword") {
            newViewData = {
                ...viewData, [name]: v,
                autoRegisterMode: ChannelAutoRegisterMode.ByApproval,
                approvalPassword: v ? viewData.suggestApprovalPassword : null
            };
        }
        else if (name === "dismissApprovalForUnits" || name === "allowInvite") {
            newViewData = {
                ...viewData, [name]: v,
                autoRegisterMode: ChannelAutoRegisterMode.ByApproval
            };
        }
        else if (name == "isPrivate" && v == true) {
            newViewData = {
                ...viewData, [name]: v,
                autoRegisterMode: ChannelAutoRegisterMode.Forbidden,
                dismissApprovalForUnits: false,
                dismissApprovalWithPassword: false,
                allowInvite: false
            };
        }

        onFormUpdate(newViewData);
        validateForm(newViewData);
    };

    ///
    const handleChangeModerator = (item: (AutoCompleteDataModel | ModeratorChannelAutoCompleteDataModel)[]) => {

        if (item.length > 0) {
            var moderator = item[0] as ModeratorChannelAutoCompleteDataModel;
            if (moderator.role < ChannelRoles.Supervisor) {
                setDialog(
                    <MprcConfirmDialog
                        open
                        severity="danger"
                        message={t("Channel.Edit.ModeratorRestrictionMessage")}
                        onClose={(yes) => {
                            if (yes) {
                                handleUpdateRecord("moderator", item[0]);
                            }
                            setDialog(null);
                        }}
                    />
                );
            } else {
                handleUpdateRecord("moderator", item[0]);
            }
        }
        else handleUpdateRecord("moderator", undefined);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (viewErrors != undefined) {
            setFormValidation({
                ...formValidation,
                name: viewErrors["name"] || null,
                approvalPassword: viewErrors["dismissApprovalWithPassword"] || null,
                unitsDismissed: viewErrors["unitsDismissedGroupIds"] || null,
                topicSuggestion: viewErrors["topicsSuggested"] || null,
            });
        }
    }, [viewErrors]);

    useEffect(() => {
        onFormValidation(Object.entries(formValidation).filter(([key, v]) => v !== null).length == 0);
    }, [formValidation]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <MprcContainer>
                <MprcCard>
                    <CardContent>
                        <MprcContainer id="box1" margin="large">
                            <Grid container spacing={2}>
                                <Grid>
                                    <MprcImageSelection
                                        variant="rounded"
                                        backgroundType="solid"
                                        allowEdit
                                        enableClear
                                        searchTerm={viewData.name}
                                        emptyImageUrl={AppImages.KnowledgeChannelThumbnail}
                                        currentImageUrl={viewData.thumbnailUrl}
                                        acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                                        imageDimensions={AppImageDimension.ThumbnailLg}
                                        onClear={() => {
                                            handleUpdateRecord("thumbnailId", null);
                                        }}
                                        onFileUploadSuccess={(data) => {
                                            handleUpdateRecord("thumbnailId", data.thumbnail ? data.thumbnail.fileId : data.mainFile?.fileId);
                                            return true;
                                        }}
                                        onFileUploadStarted={() => setFormValidation((previousState) => ({ ...previousState, thumbnail: [""] }))}
                                        onFileUploadFinished={() => setFormValidation((previousState) => ({ ...previousState, thumbnail: null }))}
                                        selectButtomText={t("Channel.Edit.SelectImageButtonText")}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <TextField
                                        fullWidth
                                        autoFocus
                                        required
                                        name="name"
                                        label={t("Channel.Edit.NameLabel")}
                                        margin="dense"
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 255 }
                                            }
                                        }}
                                        value={viewData?.name ?? ""}
                                        onChange={(e) => handleUpdateRecord("name", e.target.value)}
                                        error={viewData?.name != undefined && !!formValidation.name}
                                        helperText={viewData?.name != undefined && <MprcStringToHtml text={formValidation?.name} />}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        name="description"
                                        label={t("Channel.Edit.DescriptionLabel")}
                                        margin="dense"
                                        rows={3}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 255 }
                                            }
                                        }}
                                        value={viewData?.description ?? ""}
                                        onChange={(e) => handleUpdateRecord("description", e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </MprcContainer>

                        <MprcContainer id="box2" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.VisibilityTitle")} margin="dense" />

                            <MprcContainer id="box2" margin="normal">
                                <RadioGroup value={viewData.isPrivate} onChange={e => handleUpdateRecord("isPrivate", e.target.value == "true")}>
                                    <MprcRadio
                                        value={false}
                                        label={t("Channel.Edit.PublicChannelLabel")}
                                        helperText={
                                            <Trans
                                                i18nKey="knowledge:Channel.Edit.PublicChannelHint"
                                                components={AppTransComponents}
                                            />
                                        }
                                    />
                                    <MprcRadio value={true} label={t("Channel.Edit.PrivateChannelLabel")} helperText={t("Channel.Edit.PrivateChannelHint")}>
                                        <MprcSwitch
                                            label={t("Channel.Edit.ContainsConfidentialInfoLabel")}
                                            helperText={t("Channel.Edit.ContainsConfidentialInfoHint")}
                                            checked={viewData.containsConfidentialInfo}
                                            disabled={!viewData.isPrivate || !viewData.isOwnerOrManager}
                                            onChange={(e, checked) => handleUpdateRecord("containsConfidentialInfo", checked)}
                                        />
                                    </MprcRadio>

                                </RadioGroup>
                            </MprcContainer>

                            <MprcSwitch
                                label={t("Channel.Edit.DenyExternalUsersLabel")}
                                helperText={t("Channel.Edit.DenyExternalUsersHint")}
                                checked={viewData.denyExternalUsers}
                                onChange={(e, checked) => handleUpdateRecord("denyExternalUsers", checked)}
                            />

                        </MprcContainer>

                        <MprcContainer id="box3" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.AutoRegisterTitle")} margin="dense" />

                            <MprcContainer>
                                <RadioGroup
                                    value={viewData.autoRegisterMode}
                                    onChange={e => handleUpdateRecord("autoRegisterMode", e.target.value)}>
                                    <MprcRadio value={ChannelAutoRegisterMode.Free} label={t("Channel.Edit.FreeRegisterLabel")} helperText={t("Channel.Edit.FreeRegisterHint")} />
                                    <MprcRadio value={ChannelAutoRegisterMode.Forbidden} label={t("Channel.Edit.ForbiddenRegisterLabel")} helperText={t("Channel.Edit.ForbiddenRegisterHint")} />
                                    <MprcRadio value={ChannelAutoRegisterMode.ByApproval} label={t("Channel.Edit.ByApprovalRegisterLabel")} helperText={t("Channel.Edit.ByApprovalRegisterHint")}>
                                        <MprcSwitch
                                            label={t("Channel.Edit.DismissApprovalForUnitsLabel")}
                                            checked={viewData.dismissApprovalForUnits}
                                            onChange={(e, checked) => handleUpdateRecord("dismissApprovalForUnits", checked)}
                                        >
                                            <UnitAutoCompleteHierarchical
                                                selectedMode="assignment"
                                                selectedItems={viewData.unitsDismissed as UnitAutoCompleteHierarchicalModel[] ?? []}
                                                multiSelect={true}
                                                disabled={!viewData.dismissApprovalForUnits}
                                                showDescendantOption={true}
                                                onChange={(items) => handleUpdateRecord("unitsDismissed", items as UnitAutoCompleteHierarchicalModelDto[])}
                                                onError={onError}
                                                error={!!formValidation.unitsDismissed}
                                                helperText={<MprcStringToHtml text={formValidation?.unitsDismissed} />}
                                            />
                                        </MprcSwitch>

                                        <MprcSwitch
                                            label={t("Channel.Edit.DismissApprovalWithPwdLabel")}
                                            checked={viewData.dismissApprovalWithPassword}
                                            onChange={e => handleUpdateRecord("dismissApprovalWithPassword", e.target.checked)}
                                        >
                                            <TextField
                                                required
                                                name="dismissApprovalWithPassword"
                                                disabled={!viewData.dismissApprovalWithPassword}
                                                helperText={
                                                    !!formValidation.approvalPassword ?
                                                        <MprcStringToHtml text={formValidation.approvalPassword} /> :
                                                        <>
                                                            {t("Channel.Edit.ApprovalPwdHintL1")}
                                                            <br />
                                                            {t("Channel.Edit.ApprovalPwdHintL2")}
                                                        </>}
                                                margin="dense"
                                                slotProps={{
                                                    input: {
                                                        inputProps: { maxLength: 255 }
                                                    }
                                                }}
                                                value={viewData.approvalPassword || ""}
                                                onChange={e => handleUpdateRecord("approvalPassword", e.target.value)}
                                                error={!!formValidation.approvalPassword}
                                            />
                                        </MprcSwitch>
                                        <MprcSwitch
                                            label={t("Channel.Edit.AllowInviteLabel")}
                                            helperText={t("Channel.Edit.AllowInviteHint")}
                                            checked={viewData.allowInvite}
                                            onChange={e => handleUpdateRecord("allowInvite", e.target.checked)}
                                        />
                                    </MprcRadio>
                                </RadioGroup>
                            </MprcContainer>
                            <MprcContainer margin="dense">
                                <FormLabel>{t("Channel.Edit.AutoRegisterRoleLabel")}</FormLabel>
                                <RadioGroup value={viewData.autoRegisterRole} onChange={e => handleUpdateRecord("autoRegisterRole", e.target.value)}>
                                    <MprcRadio value={ChannelRoles.Contributor} label={t("Channel.Edit.ContributorLabel")} />
                                    <MprcRadio value={ChannelRoles.Guest} label={t("Channel.Edit.GuestLabel")} sx={{ mt: -1 }} />
                                </RadioGroup>
                            </MprcContainer>
                        </MprcContainer>

                        <MprcContainer id="box4" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.ModeratorTitle")} margin="dense" />
                            <ModeratorChannelAutoComplete
                                margin="dense"
                                label={t("Channel.Edit.ModeratorLabel")}
                                channelId={viewData.channelId == NIL ? undefined : viewData.channelId}
                                onChange={items => handleChangeModerator(items)}
                                selectedItems={viewData.moderator != null ? [viewData.moderator] as ModeratorChannelAutoCompleteDataModel[] : []}
                                multiSelect={false}
                                onError={e => onError(e)}
                                error={!!formValidation.moderator}
                                helperText={<MprcStringToHtml text={formValidation?.moderator} />}
                            />

                            <MprcSwitch
                                margin="dense"
                                label={t("Channel.Edit.PreferModerationByManagerLabel")}
                                helperText={t("Channel.Edit.PreferModerationByManagerHint")}
                                checked={viewData.preferModerationByManager}
                                onChange={(e) => handleUpdateRecord("preferModerationByManager", e.target.checked)}
                                sx={{ mt: 1 }}
                            />
                        </MprcContainer>

                        <MprcContainer id="box5" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.PublicationApprovalTitle")} subtitle={t("Channel.Edit.PublicationApprovalHint")} margin="dense" />
                            <RadioGroup value={viewData.approvalMode} onChange={e => handleUpdateRecord("approvalMode", e.target.value)}>
                                <MprcRadio value={ChannelApprovalMode.None} label={t("Channel.Edit.NoneModerationLabel")} helperText={t("Channel.Edit.NoneModerationHint")} />
                                <MprcRadio value={ChannelApprovalMode.AI} label={t("Channel.Edit.IAModerationLabel")} helperText={t("Channel.Edit.IAModerationHint")} />
                                <MprcRadio value={ChannelApprovalMode.Required} label={t("Channel.Edit.RequiredModerationLabel")} helperText={t("Channel.Edit.RequiredModerationHint")} />
                            </RadioGroup>
                        </MprcContainer>

                        <MprcContainer id="box6" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.TopicSuggestionsTitle")} subtitle={t("Channel.Edit.TopicSuggestionsHint")} margin="dense" />
                            <TopicAutoCompleteHierarchical
                                margin="dense"
                                selectedMode="free"
                                selectedItems={(viewData.topicsSuggested ?? []) as AutoCompleteHierarchicalModel[]}
                                multiSelect={true}
                                onChange={(item: AutoCompleteHierarchicalModel[]) => handleUpdateRecord("topicsSuggested", item)}
                                onError={onError}
                                placeholder={t("Channel.Edit.TopicSuggestionPlaceholder")}
                                helperText={!!formValidation.topicSuggestion ? <MprcStringToHtml text={formValidation?.topicSuggestion} /> : t("Channel.Edit.TopicSuggestionHint")}
                                maxSelectItems={5}
                                error={!!formValidation.topicSuggestion}
                            />
                        </MprcContainer>

                        <MprcContainer id="box6" margin="large">
                            <MprcFormSubHeader title={t("Channel.Edit.QuickFilterTitle")} margin="dense" />
                            <RadioGroup value={viewData.useCustomQuickFilter}
                                onChange={(e) => handleUpdateRecord("useCustomQuickFilter", e.target.value == "true")}
                            >
                                <MprcContainer margin="dense">
                                    <MprcRadio
                                        value={false}
                                        label={t("Channel.Edit.QuickFilterDefaultLabel")}
                                        helperText={t("Channel.Edit.QuickFilterDefaultHint")}
                                    />
                                </MprcContainer>
                                <MprcRadio
                                    value={true}
                                    label={t("Channel.Edit.QuickFilterCustomLabel")}
                                    helperText={
                                        <>
                                            {t("Channel.Edit.QuickFilterCustomHintL1")}
                                            {t("Channel.Edit.QuickFilterCustomHintL2")}
                                        </>}
                                >
                                    {viewData.useCustomQuickFilter &&
                                        <MprcButton
                                            variant="contained"
                                            disabled={recordChanged && viewData.channelId != NIL}
                                            color="primary"
                                            text={t("Channel.Edit.QuickFilterCustomButton")}
                                            sx={{ mt: 1 }}
                                            onClick={() => navigate(KnowledgePaths.BuildEditChannelQuickFilters(viewData.channelId))}
                                        />
                                    }
                                </MprcRadio>
                            </RadioGroup>
                        </MprcContainer>

                        <MprcContainer id="box7" margin="none">
                            <MprcFormSubHeader title={t("Channel.Edit.OtherOptionsTitle")} margin="dense" />
                            <MprcSwitch
                                margin="dense"
                                label={t("Channel.Edit.AllowNonMemberCommentsLabel")}
                                checked={viewData.allowNonMemberComments}
                                onChange={e => handleUpdateRecord("allowNonMemberComments", e.target.checked)}
                            />

                            <MprcSwitch
                                margin="dense"
                                label={t("Channel.Edit.EnableIaQuestionAnsweringLabel")}
                                checked={viewData.enableIaQuestionAnswering}
                                onChange={(e) => handleUpdateRecord("enableIaQuestionAnswering", e.target.checked)}
                            />

                            <MprcSwitch
                                margin="dense"
                                label={t("Channel.Edit.AllowSupervisorEditLabel")}
                                helperText={!viewData.canEditAllowSupervisorEdit ? t("Channel.Edit.AllowSupervisorEditHint") : undefined}
                                checked={viewData.allowSupervisorEdit}
                                onChange={(e) => handleUpdateRecord("allowSupervisorEdit", e.target.checked)}
                                disabled={!viewData.canEditAllowSupervisorEdit}
                            />

                            <MprcSwitch
                                margin="dense"
                                label={t("Channel.Edit.RequirePublicationEditJustification")}
                                helperText={t("Channel.Edit.RequirePublicationEditJustificationHint")}
                                checked={viewData.requirePublicationEditJustification}
                                onChange={(e) => handleUpdateRecord("requirePublicationEditJustification", e.target.checked)}
                            />

                        </MprcContainer>
                    </CardContent>
                </MprcCard>
            </MprcContainer >
        </>
    );
};
